import React, { useState } from 'react';
import { X, ChevronDown, ChevronUp, BarChart3, Copy, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import { createPortal } from 'react-dom';
import PersonaReactions from './PersonaReactions';
import ExpertRecommendations from './ExpertRecommendations';
import EnhancedTableView from '../EnhancedTableView';

// Type definitions based on the provided JSON structure
interface ABTestCreativesDimension {
  dimension: string;
  score: number;
  justification: string;
}
// legacy table
interface ABTestCreativesRankingRow {
  rank: number;
  overall_score: number;
  resonance_with_needs_values: number;
  summary_rationale: string;
  ad_index: number;
}

interface ABTestCreativesRankingTable {
  title: string;
  headers: string[];
  rows: ABTestCreativesRankingRow[];
}

// New Creative Performance Row interface for the enhanced table (backward compatible)
interface CreativePerformanceRow {
  rank: number;
  creative: string;
  overall_score: number;
  summary_rationale: string;
  top_3_recommendations: string[];
  critical_changes: string[];
  priority_changes: string[];
  good_to_fix: string[];
}

interface CreativePerformanceOverviewTable {
  title: string;
  headers: string[];
  rows: CreativePerformanceRow[];
}

interface ABTestCreativesSummary {
  title: string;
  headers: string[];
  rows: ABTestCreativesDimension[];
}

interface ABTestCreativesWinningAd {
  ad_title: string;
  ad_index: number;
  summary: ABTestCreativesSummary;
  expert_recommendations: string;
}

interface ABTestCreativesOverallRanking {
  winner: number;
  scores: number[];
  ranking_table: ABTestCreativesRankingTable | CreativePerformanceOverviewTable;
}

interface ABTestCreativesOutput {
  overall_ranking: ABTestCreativesOverallRanking;
  winning_ad?: ABTestCreativesWinningAd;
}

interface ABTestCreativesPersonaReaction {
  title: string;
  headers: string[];
  rows: Array<[string, string, number]>;
}

interface ABTestCreativesDetailedAnalysis {
  rank: number;
  ad_title: string;
  ad_index: number;
  persona_reactions: ABTestCreativesPersonaReaction;
  summary?: ABTestCreativesSummary;
  expert_recommendations?: string;
}

interface ABTestCreativesAnalysis {
  detailed_analysis: ABTestCreativesDetailedAnalysis[];
}

interface ABTestCreativesData {
  winner: number;
  scores: number[];
  output: ABTestCreativesOutput;
  analysis: ABTestCreativesAnalysis;
  // Additional fields for image support
  images?: string[];
  image_descriptions?: string[];
  // New field for enhanced performance data (backward compatible)
  performance_rows?: CreativePerformanceRow[];
}

interface ABTestCreativesResultsProps {
  data: ABTestCreativesData;
  contentType?: string;
  isExporting?: boolean;
  className?: string;
}

const ABTestCreativesResults: React.FC<ABTestCreativesResultsProps> = ({
  data,
  isExporting = false,
  className = '',
}) => {
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [expandedDetailedAnalysis, setExpandedDetailedAnalysis] = useState<{
    [key: number]: boolean;
  }>(() => {
    // Initialize all expert recommendations to be expanded by default
    const initialState: { [key: number]: boolean } = {};
    if (data && data.analysis && data.analysis.detailed_analysis) {
      data.analysis.detailed_analysis.forEach((creative) => {
        if (creative.expert_recommendations) {
          initialState[creative.ad_index || 0] = true;
        }
      });
    }
    return initialState;
  });
  const [showDetailedAnalysisSection, setShowDetailedAnalysisSection] = useState(false);
  const [expandedTableRows, setExpandedTableRows] = useState<{ [key: number]: boolean }>({});

  // Auto-expand all sections when exporting
  React.useEffect(() => {
    if (isExporting) {
      // Expand all detailed analysis sections
      const allExpanded: { [key: number]: boolean } = {};
      if (data && data.analysis && data.analysis.detailed_analysis) {
        data.analysis.detailed_analysis.forEach((creative) => {
          allExpanded[creative.ad_index || 0] = true;
        });
      }
      setExpandedDetailedAnalysis(allExpanded);
      setShowDetailedAnalysisSection(true);
    }
  }, [isExporting, data]);
  const [copied, setCopied] = useState(false);

  if (!data) {
    return (
      <div className={`space-y-6 ${className}`}>
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">AB Test Creatives Results</h3>
          <div className="text-gray-600 text-sm">No AB test results data available</div>
        </div>
      </div>
    );
  }

  if (!data.output || !data.analysis) {
    return (
      <div className={`space-y-6 ${className}`}>
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">AB Test Creatives Results</h3>
          <div className="text-gray-600 text-sm">
            Incomplete AB test results data. Missing: {!data.output ? 'output' : ''}{' '}
            {!data.analysis ? 'analysis' : ''}
          </div>
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-semibold text-sm text-gray-700 mb-2">Available Data Keys:</h4>
            <pre className="text-xs text-gray-600">
              {JSON.stringify(Object.keys(data), null, 2)}
            </pre>
          </div>
        </div>
      </div>
    );
  }

  const overallRanking = data.output.overall_ranking;
  const detailedAnalysis = data.analysis.detailed_analysis;

  // Check if overall_ranking exists and has the expected structure
  if (!overallRanking || !overallRanking.ranking_table) {
    return (
      <div className={`space-y-6 ${className}`}>
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">AB Test Creatives Results</h3>
          <div className="text-gray-600 text-sm">
            Invalid AB test results structure. Missing overall ranking data.
          </div>
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-semibold text-sm text-gray-700 mb-2">Output Structure:</h4>
            <pre className="text-xs text-gray-600">{JSON.stringify(data.output, null, 2)}</pre>
          </div>
        </div>
      </div>
    );
  }

  // Helper functions for image handling
  const getImageSrc = (imageData: string) => {
    if (imageData.startsWith('data:image/')) {
      return imageData;
    }
    return `data:image/jpeg;base64,${imageData}`;
  };

  const handleImageClick = (imageSrc: string) => {
    if (!isExporting) {
      setSelectedImage(imageSrc);
      setShowImageModal(true);
    }
  };

  const toggleDetailedAnalysis = (adIndex: number) => {
    setExpandedDetailedAnalysis((prev) => ({
      ...prev,
      [adIndex]: !prev[adIndex],
    }));
  };

  // Helper functions for backward compatibility
  const isNewPerformanceData = (
    rankingTable: any,
  ): rankingTable is CreativePerformanceOverviewTable => {
    return (
      rankingTable &&
      rankingTable.rows &&
      rankingTable.rows.length > 0 &&
      'creative' in rankingTable.rows[0] &&
      'top_3_recommendations' in rankingTable.rows[0]
    );
  };

  const formatListItems = (items: string[]) => {
    if (!items || items.length === 0) return 'None';
    return items.map((item, index) => `${index + 1}. ${item}`).join(' ');
  };

  const getCreativeIndexFromName = (creativeName: string): number => {
    const match = creativeName.match(/Creative (\d+)/);
    return match ? parseInt(match[1]) - 1 : 0;
  };

  const toggleTableRow = (rowIndex: number) => {
    setExpandedTableRows((prev) => ({
      ...prev,
      [rowIndex]: !prev[rowIndex],
    }));
  };

  // Copy and download helper functions
  const generateTableMarkdown = (rankingTable: CreativePerformanceOverviewTable) => {
    const headers = [
      'Rank',
      'Creative',
      'Average Score',
      'Summary Rationale',
      'Top 3 Recommendations',
      'Critical Changes',
      'Priority Changes',
      'Good to Fix',
    ];
    const headerRow = `| ${headers.join(' | ')} |`;
    const separator = `|${headers.map(() => ' --- ').join('|')}|`;
    const rows = rankingTable.rows.map((row) => {
      return `| ${row.rank} | ${row.creative} | ${row.overall_score.toFixed(1)} | ${row.summary_rationale} | ${formatListItems(row.top_3_recommendations)} | ${formatListItems(row.critical_changes)} | ${formatListItems(row.priority_changes)} | ${formatListItems(row.good_to_fix)} |`;
    });
    return [headerRow, separator, ...rows].join('\n');
  };

  const generateTableExcel = (rankingTable: CreativePerformanceOverviewTable) => {
    const headers = [
      'Rank',
      'Creative',
      'Average Score',
      'Summary Rationale',
      'Top 3 Recommendations',
      'Critical Changes',
      'Priority Changes',
      'Good to Fix',
    ];
    const wsData = [
      headers,
      ...rankingTable.rows.map((row) => [
        row.rank,
        row.creative,
        row.overall_score.toFixed(1),
        row.summary_rationale,
        formatListItems(row.top_3_recommendations),
        formatListItems(row.critical_changes),
        formatListItems(row.priority_changes),
        formatListItems(row.good_to_fix),
      ]),
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Creative Performance Overview');
    return XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  };

  const handleCopyTable = async (rankingTable: CreativePerformanceOverviewTable) => {
    try {
      const markdown = generateTableMarkdown(rankingTable);
      await navigator.clipboard.writeText(markdown);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const handleDownloadExcel = (rankingTable: CreativePerformanceOverviewTable) => {
    try {
      const excelData = generateTableExcel(rankingTable);
      const blob = new Blob([excelData], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'creative-performance-overview.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download: ', err);
    }
  };

  const renderExpandableCell = (content: any, rowIndex: number, maxLength: number = 100) => {
    // Convert content to string if it's not already
    let textContent: string;
    if (typeof content === 'string') {
      textContent = content;
    } else if (content === null || content === undefined) {
      return <span>None</span>;
    } else {
      // If it's an object, try to extract meaningful text or stringify it
      textContent = String(content);
    }

    const isRowExpanded = expandedTableRows[rowIndex];

    if (textContent.length <= maxLength) {
      return <span>{textContent}</span>;
    }

    if (isRowExpanded) {
      return (
        <div>
          <span>{textContent}</span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleTableRow(rowIndex);
            }}
            className="ml-2 text-blue-600 hover:text-blue-800 underline text-sm"
          >
            Show less
          </button>
        </div>
      );
    } else {
      return (
        <div>
          <span>{textContent.substring(0, maxLength)}...</span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleTableRow(rowIndex);
            }}
            className="ml-2 text-blue-600 hover:text-blue-800 underline text-sm"
          >
            Show more
          </button>
        </div>
      );
    }
  };

  const renderCustomPerformanceTable = (rankingTable: CreativePerformanceOverviewTable) => {
    const headers = [
      'Rank',
      'Image',
      'Creative',
      'Average Score',
      'Summary Rationale',
      'Top 3 Recommendations',
      'Critical Changes',
      'Priority Changes',
      'Good to Fix',
    ];

    return (
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Creative Performance Overview</h3>
            <p className="text-sm text-gray-600">({rankingTable.rows.length} creatives analyzed)</p>
          </div>
          {!isExporting && (
            <div className="flex space-x-2">
              <button
                onClick={() => handleCopyTable(rankingTable)}
                className="flex items-center space-x-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#07E5D1]"
              >
                <Copy className="h-4 w-4" />
                <span>{copied ? 'Copied!' : 'Copy'}</span>
              </button>
              <button
                onClick={() => handleDownloadExcel(rankingTable)}
                className="flex items-center space-x-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#07E5D1]"
              >
                <Download className="h-4 w-4" />
                <span>Excel</span>
              </button>
            </div>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {headers.map((header, index) => (
                  <th
                    key={index}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rankingTable.rows.map((row, rowIndex) => (
                <tr key={rowIndex} className="hover:bg-gray-50">
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{row.rank}</td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    {data.images && data.images[getCreativeIndexFromName(row.creative)] ? (
                      <img
                        src={
                          data.images[getCreativeIndexFromName(row.creative)].startsWith(
                            'data:image/',
                          )
                            ? data.images[getCreativeIndexFromName(row.creative)]
                            : `data:image/jpeg;base64,${data.images[getCreativeIndexFromName(row.creative)]}`
                        }
                        alt={row.creative}
                        className="w-12 h-16 object-cover rounded border cursor-pointer hover:border-[#07E5D1]"
                        onClick={() =>
                          handleImageClick(
                            getImageSrc(data.images![getCreativeIndexFromName(row.creative)]),
                          )
                        }
                      />
                    ) : (
                      <div className="w-12 h-16 bg-gray-100 rounded border flex items-center justify-center">
                        <span className="text-gray-400 text-xs">No Image</span>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {row.creative}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {row.overall_score.toFixed(1)}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-900 max-w-xs">
                    {renderExpandableCell(row.summary_rationale, rowIndex)}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-900 max-w-xs">
                    {renderExpandableCell(formatListItems(row.top_3_recommendations), rowIndex)}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-900 max-w-xs">
                    {renderExpandableCell(formatListItems(row.critical_changes), rowIndex)}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-900 max-w-xs">
                    {renderExpandableCell(formatListItems(row.priority_changes), rowIndex)}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-900 max-w-xs">
                    {renderExpandableCell(formatListItems(row.good_to_fix), rowIndex)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderPerformanceTable = () => {
    const rankingTable = overallRanking.ranking_table;

    if (isNewPerformanceData(rankingTable)) {
      // Render new Creative Performance Overview table with expandable cells
      return renderCustomPerformanceTable(rankingTable);
    } else {
      // Render legacy Performance Summary table
      const legacyRankingTable = rankingTable as ABTestCreativesRankingTable;
      return (
        <EnhancedTableView
          title="Performance Summary"
          headers={[
            'Rank',
            'Image',
            'Average Score',
            'Resonance Score',
            'Summary Rationale',
            'Creative',
          ]}
          data={legacyRankingTable.rows.map((row) => [
            row.rank.toString(),
            '', // Placeholder for image column - will be populated by EnhancedTableView
            row.overall_score.toFixed(1),
            row.resonance_with_needs_values.toFixed(1),
            row.summary_rationale,
            `Creative ${row.ad_index + 1}`,
          ])}
          maxInitialRows={5}
          showFullContent={true}
          showExportButtons={!isExporting}
          images={data.images || []}
          onImageClick={handleImageClick}
        />
      );
    }
  };

  return (
    <div className={`space-y-8 ${className}`}>
      {/* Performance Table - Supports both old and new formats with backward compatibility */}
      {overallRanking.ranking_table.rows &&
        overallRanking.ranking_table.rows.length > 0 &&
        renderPerformanceTable()}

      {/* Winner Showcase - Hidden per user request */}
      {/* {hasWinner && data.output.winning_ad && (
        <WinnerCard 
          winningAd={data.output.winning_ad}
          images={data.images}
          rankingData={overallRanking.ranking_table.rows}
          isExporting={isExporting}
        />
      )} */}

      {/* Detailed Analysis Dropdown Section */}
      {detailedAnalysis && detailedAnalysis.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <button
            onClick={() => setShowDetailedAnalysisSection(!showDetailedAnalysisSection)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center space-x-3">
              <BarChart3 className="h-5 w-5 text-gray-600" />
              <span className="font-medium text-gray-900">Detailed Analysis</span>
              <span className="text-xs text-gray-500">({detailedAnalysis.length} creatives)</span>
            </div>
            {showDetailedAnalysisSection ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </button>

          {showDetailedAnalysisSection && (
            <div className="border-t border-gray-100 p-6 space-y-6">
              {detailedAnalysis
                .sort((a, b) => (a.rank || 0) - (b.rank || 0))
                .map((creative, index) => (
                  <div
                    key={creative.ad_index || index}
                    className="bg-white rounded-lg border border-gray-200 p-6 space-y-6"
                  >
                    {/* Enhanced Creative Header */}
                    <div className="flex space-x-6 border-b border-gray-100 pb-6">
                      {/* Creative Image - Full Height Column */}
                      <div className="flex-shrink-0">
                        {data.images && data.images[creative.ad_index || 0] ? (
                          <div
                            className="cursor-pointer group"
                            onClick={() =>
                              handleImageClick(getImageSrc(data.images![creative.ad_index || 0]))
                            }
                          >
                            <img
                              src={
                                data.images[creative.ad_index || 0].startsWith('data:image/')
                                  ? data.images[creative.ad_index || 0]
                                  : `data:image/jpeg;base64,${data.images[creative.ad_index || 0]}`
                              }
                              alt={creative.ad_title || 'Creative'}
                              className="w-24 h-32 object-cover rounded-lg border border-gray-200 group-hover:border-[#07E5D1] transition-colors"
                            />
                          </div>
                        ) : (
                          <div className="w-24 h-32 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
                            <span className="text-gray-400 text-xs text-center">
                              No
                              <br />
                              Image
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Creative Info & Stats - Full Content Column */}
                      <div className="flex-1 min-w-0 flex flex-col justify-between">
                        {/* Top: Title only - removing duplicate score */}
                        <div className="flex items-start justify-between mb-6">
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900 mb-1">
                              {(creative.rank || 0) === 1 ? '🏆 ' : ''}#{creative.rank || 0} -{' '}
                              {creative.ad_title || 'Untitled Creative'}
                            </h3>
                            <p className="text-sm text-gray-500">
                              Creative {(creative.ad_index || 0) + 1}
                            </p>
                          </div>
                        </div>

                        {/* Bottom: Persona Reaction Summary */}
                        {creative.persona_reactions && (
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                              <div className="text-lg font-semibold text-gray-900">
                                {
                                  creative.persona_reactions.rows.filter((row) => row[2] >= 8.0)
                                    .length
                                }{' '}
                                customers
                              </div>
                              <div className="text-xs text-gray-600">Rated 8+ out of 10</div>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                              <div className="text-lg font-semibold text-gray-900">
                                {
                                  creative.persona_reactions.rows.filter(
                                    (row) => row[2] >= 6.0 && row[2] < 8.0,
                                  ).length
                                }{' '}
                                customers
                              </div>
                              <div className="text-xs text-gray-600">Rated 6-7.9 out of 10</div>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                              <div className="text-lg font-semibold text-gray-900">
                                {
                                  creative.persona_reactions.rows.filter((row) => row[2] < 6.0)
                                    .length
                                }{' '}
                                customers
                              </div>
                              <div className="text-xs text-gray-600">Rated &lt;6 out of 10</div>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                              <div className="text-lg font-semibold text-[#07E5D1]">
                                {(
                                  creative.persona_reactions.rows.reduce(
                                    (sum, row) => sum + row[2],
                                    0,
                                  ) / creative.persona_reactions.rows.length
                                ).toFixed(1)}
                              </div>
                              <div className="text-xs text-gray-600">Avg Reaction</div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Expert Recommendations - Show first and make collapsible - Only if recommendations exist */}
                    {creative.expert_recommendations &&
                      typeof creative.expert_recommendations === 'string' &&
                      creative.expert_recommendations.trim() !== '' &&
                      creative.expert_recommendations !== 'See performance analysis' && (
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                          <button
                            onClick={() => toggleDetailedAnalysis(creative.ad_index || 0)}
                            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex items-center space-x-3">
                              <BarChart3 className="h-5 w-5 text-gray-600" />
                              <span className="font-medium text-gray-900">
                                Expert Recommendations
                              </span>
                            </div>
                            {expandedDetailedAnalysis[creative.ad_index || 0] ? (
                              <ChevronUp className="h-5 w-5 text-gray-400" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-gray-400" />
                            )}
                          </button>

                          {expandedDetailedAnalysis[creative.ad_index || 0] && (
                            <div className="border-t border-gray-100 p-6">
                              <ExpertRecommendations
                                recommendations={creative.expert_recommendations}
                                adTitle={creative.ad_title || 'Creative'}
                                isExporting={isExporting}
                              />
                            </div>
                          )}
                        </div>
                      )}

                    {/* Performance Scorecard - Show second */}
                    {creative.summary && (
                      <EnhancedTableView
                        title="Performance Scorecard"
                        headers={
                          creative.summary.headers || ['Dimension', 'Score', 'Justification']
                        }
                        data={
                          creative.summary.rows?.map((row) => [
                            row.dimension || 'Unknown',
                            (row.score || 0).toFixed(1),
                            row.justification || 'No justification available',
                          ]) || []
                        }
                        maxInitialRows={8}
                        showFullContent={true}
                        showExportButtons={!isExporting}
                        lastRowIsSummary={true}
                      />
                    )}

                    {/* Persona Reactions - Show last */}
                    {creative.persona_reactions && (
                      <PersonaReactions
                        personaReactions={creative.persona_reactions}
                        adTitle={creative.ad_title || 'Creative'}
                        isExporting={isExporting}
                      />
                    )}
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {/* Image Modal */}
      {showImageModal &&
        selectedImage &&
        createPortal(
          <div
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
            onClick={() => setShowImageModal(false)}
          >
            <div className="relative max-w-4xl max-h-4xl p-4">
              <button
                onClick={() => setShowImageModal(false)}
                className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
              >
                <X className="h-8 w-8" />
              </button>
              <img
                src={selectedImage}
                alt="Creative"
                className="max-w-full max-h-full object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default ABTestCreativesResults;
